import Vue from "vue";
import Router from "vue-router";
// import VueRouteMiddleware from "vue-route-middleware";
import { runMiddlewares } from "@/router/middlewareRunner";
import {
  auth,
  checkFunction,
  checkOrgStatus,
  guest,
  trialPeriod,
  organizationTrial,
  checkPermission403,
  checkOwner
} from "src/router/middleware";
import i18n from "src/services/i18n";
import { PLAN_FUNCTION, MENU_CODE } from "src/helpers/constant";

Vue.use(Router);

const routes = [
  {
    path: "*",
    name: "Error404",
    component: () => import("src/errors/error_404"),
    meta: {
      title: i18n.t("side_bar.error_404"),
    },
  },
  //#region Require auth
  {
    path: "/",
    component: () => import("src/components/body"),
    meta: { requiresAuth: true },
    children: [
      //#region Home
      {
        path: "",
        name: "Home",
        component: () => import("src/views/admin/Home"),
        meta: {
          title: i18n.t("side_bar.home"),
          middleware: [auth, trialPeriod],
        },
      },
      //#endregion

      //#region Password change
      {
        path: "/password/change",
        name: "PasswordChange",
        component: () => import("src/views/auth/PasswordChange"),
        meta: {
          title: "Password Change",
          middleware: [auth, trialPeriod, checkOrgStatus],
        },
      },
      //#endregion

      //#region Admins
      // {
      //   path: "/admins",
      //   name: "AdminList",
      //   component: () => import("src/views/admin/admin/AdminList"),
      //   meta: {
      //     title: i18n.t("page_admin_list.page_title"),
      //     middleware: [auth, checkFunction],
      //   },
      // },
      //#endregion

      //#region Users
      {
        path: "/users",
        name: "UserList",
        component: () => import("src/views/admin/user/UserList"),
        meta: {
          title: i18n.t("side_bar.user_list"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.USER, code: MENU_CODE.GROUP_LIST },
        },
      },
      {
        path: "/users/:id",
        name: "UserDetail",
        component: () => import("src/views/admin/user/UserDetail"),
        meta: {
          title: i18n.t("side_bar.user_detail"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.USER, code: MENU_CODE.GROUP_LIST },
        },
      },
      {
        path: "/users/:id/edit",
        name: "UserEdit",
        component: () => import("src/views/admin/user/UserEdit"),
        meta: {
          title: i18n.t("side_bar.user_edit"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.USER, code: MENU_CODE.GROUP_LIST },
        },
      },
      {
        path: "/users/:id/contact",
        name: "ContactList",
        component: () => import("src/views/admin/user/ContactList"),
        meta: {
          title: i18n.t("side_bar.contact_list"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.USER, code: MENU_CODE.GROUP_LIST },
        },
      },
      //#endregion

      //#region Groups
      {
        path: "/groups",
        name: "GroupList",
        component: () => import("src/views/admin/group/GroupList"),
        meta: {
          title: i18n.t("side_bar.group_list"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.GROUP, code: MENU_CODE.GROUP_LIST },
        },
      },
      {
        path: "/groups/add",
        name: "GroupAdd",
        component: () => import("src/views/admin/group/GroupAdd"),
        meta: {
          title: i18n.t("side_bar.group_add"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.GROUP, code: MENU_CODE.GROUP_ADD },
        },
        props: { isEdit: false },
      },
      {
        path: "/groups/:id",
        name: "GroupDetail",
        component: () => import("src/views/admin/group/GroupDetail"),
        meta: {
          title: i18n.t("side_bar.group_detail"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.GROUP, code: MENU_CODE.GROUP_LIST },
        },
      },
      {
        path: "/groups/:id/edit",
        name: "GroupEdit",
        component: () => import("src/views/admin/group/GroupEdit"),
        meta: {
          title: i18n.t("side_bar.group_edit"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.GROUP, code: MENU_CODE.GROUP_LIST },
        },
      },
      //#endregion

      //#region Plans
      {
        path: "/plans",
        name: "PlanDetail",
        component: () => import("src/views/admin/plan/PlanDetail"),
        meta: {
          title: i18n.t("side_bar.plan_detail"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.PLAN_DETAIL },
        },
      },
      {
        path: "/plans/change",
        name: "PlanChange",
        component: () => import("src/views/admin/plan/PlanChange"),
        props: true,
        meta: {
          title: i18n.t("side_bar.plan_change"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.PLAN_CHANGE },
        },
      },
      {
        path: "/plans/stop",
        name: "RequestStopService",
        component: () => import("src/views/admin/plan/RequestStopService"),
        meta: {
          title: i18n.t("side_bar.request_stop_service"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403 ],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.REQUEST_STOP_SERVICE },
        },
      },
      //#endregion

      //#region Licenses
      {
        path: "/licenses/change",
        name: "LicenseChange",
        component: () => import("src/views/admin/plan/LicenseChange"),
        meta: {
          title: i18n.t("side_bar.license_change"),
          functions: ["charge_business"],
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.LICENSE_CHANGE },
        },
      },
      {
        path: "/showroom-external-concurrent-access/change",
        name: "GuestAccessLicenseChange",
        component: () =>
          import("src/views/admin/plan/GuestAccessLicenseChange"),
        meta: {
          title: i18n.t("side_bar.license_change"),
          functions: ["charge_visitor"],
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.GUEST_ACCESS_CHANGE },
        },
      },
      //#endregion

      //#region Storages
      {
        path: "/storages",
        name: "StorageList",
        component: () => import("src/views/admin/plan/StorageList"),
        meta: {
          title: i18n.t("side_bar.storage_list"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.STORAGE_LIST },
        },
      },
      {
        path: "/storages/:id/confirm",
        name: "StorageConfirm",
        component: () => import("src/views/admin/plan/StorageConfirm"),
        props: true,
        meta: {
          title: i18n.t("side_bar.storage_confirm"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.STORAGE_LIST },
        },
      },
      //#endregion

      //#region Subscription
      {
        path: "/subscription/create/:id",
        name: "SubscriptionCreate",
        component: () =>
          import("@/views/admin/subscription/SubscriptionCreate"),
        meta: {
          title: i18n.t("page_subscription_create.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PLAN, code: MENU_CODE.PLAN_DETAIL },
        },
      },

      //#endregion

      //#region Payments
      {
        path: "/payments",
        name: "PaymentHistory",
        component: () => import("src/views/admin/payment/PaymentHistory"),
        meta: {
          title: i18n.t("side_bar.payment_history"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PAYMENT, code: MENU_CODE.PAYMENT_HISTORY },
        },
      },
      {
        path: "/payment/:id",
        name: "PaymentDetail",
        component: () => import("src/views/admin/payment/PaymentDetail"),
        meta: {
          title: i18n.t("side_bar.payment_detail"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PAYMENT, code: MENU_CODE.PAYMENT_HISTORY },
        },
      },
      {
        path: "/payment/retry/:id",
        name: "RetryPayment",
        component: () => import("src/views/admin/payment/RetryPayment"),
        meta: {
          title: i18n.t("side_bar.retry_payment"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PAYMENT, code: MENU_CODE.PAYMENT_HISTORY },
        },
      },
      {
        path: "/payment/new-circle/:id",
        name: "NewCirclePayment",
        component: () => import("src/views/admin/payment/NewCirclePayment"),
        meta: {
          title: i18n.t("side_bar.new_circle_payment"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PAYMENT, code: MENU_CODE.PAYMENT_HISTORY },
        },
      },
      {
        path: "/card/add",
        name: "PaymentAddCard",
        component: () => import("src/views/admin/payment/PaymentAddCard"),
        meta: {
          title: i18n.t("side_bar.add_card"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PAYMENT, code: MENU_CODE.PAYMENT_CARD },
        },
      },
      //#endregion

      //#region Security
      {
        path: "/security",
        name: "Security",
        component: () => import("src/views/admin/security/Security"),
        meta: {
          title: i18n.t("side_bar.security"),
          functions: ["use_sercurity"],
          middleware: [auth, trialPeriod, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.SECURITY, code: MENU_CODE.SECURITY },
        },
      },
      //#endregion

      //#region Rooms
      // {
      //   path: "/rooms",
      //   name: "RoomList",
      //   component: () => import("src/views/admin/rooms/RoomList"),
      //   meta: {
      //     title: i18n.t("side_bar.rooms"),
      //     middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
      //     permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
      //   },
      // },
      {
        path: "/list-rooms",
        name: "ListRoom",
        component: () => import("src/views/admin/rooms/ListRoom"),
        meta: {
          title: i18n.t("side_bar.rooms"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      // {
      //   path: "/rooms/add",
      //   name: "RoomAdd",
      //   component: () => import("src/views/admin/rooms/RoomAdd"),
      //   meta: {
      //     title: i18n.t("page_room_add.page_title"),
      //     functions: [PLAN_FUNCTION.OPERATOR_MANAGEMENT],
      //     middleware: [auth, organizationTrial, checkFunction],
      //   },
      // },
      {
        path: "/rooms/edit/:id",
        name: "RoomEdit",
        component: () => import("src/views/admin/rooms/RoomEdit"),
        meta: {
          title: i18n.t("page_room_edit.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/rooms/entry-history/:id",
        name: "RoomHistory",
        component: () => import("src/views/admin/rooms/RoomHistory"),
        meta: {
          title: i18n.t("side_bar.entry_history"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/rooms/statistics",
        name: "RoomStatistics",
        component: () => import("src/views/admin/rooms/RoomStatistics"),
        meta: {
          title: i18n.t("side_bar.statistics"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/statistics/:id",
        name: "StatisticsDetail",
        component: () => import("src/views/admin/rooms/StatisticsDetail"),
        meta: {
          title: i18n.t("side_bar.statistics"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/3d-room-space/change",
        name: "RoomSpaceLicenseChange",
        component: () => import("src/views/admin/rooms/RoomSpaceLicenseChange"),
        meta: {
          title: i18n.t("side_bar.license_change"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_SPACE_CHANGE },
        },
      },
      {
        path: "/guest-verification",
        name: "GuestVerification",
        component: () => import("src/views/admin/rooms/GuestVerification"),
        meta: {
          title: i18n.t("side_bar.guest_verification"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.GUEST_VERIFICATION },
        },
      },
      {
        path: "/template-rooms/shop",
        name: "RoomShopping",
        component: () => import("src/views/admin/rooms/RoomShopping"),
        meta: {
          title: i18n.t("side_bar.room_shop"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/template-rooms/shop/detail/:id",
        name: "RoomShoppingDetail",
        component: () => import("src/views/admin/rooms/RoomShoppingDetail"),
        meta: {
          title: i18n.t("side_bar.room_shop_detail"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/template-rooms/shop/payment",
        name: "RoomShoppingPayment",
        component: () => import("src/views/admin/rooms/RoomShoppingPayment"),
        meta: {
          title: i18n.t("side_bar.room_shop_detail"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOM_LIST },
        },
      },
      {
        path: "/purchased-rooms/detail/:id",
        name: "RoomPurchasedDetail",
        component: () => import("src/views/admin/rooms/RoomPurchasedDetail"),
        meta: {
          title: i18n.t("page_room_items_buy.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOMS },
        },
      },
      {
        path: "/purchased-rooms/history/:id",
        name: "RoomPurchasedHistory",
        component: () => import("src/views/admin/rooms/RoomPurchasedHistory"),
        meta: {
          title: i18n.t("page_room_items_detail.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOMS },
        },
      },
      // {
      //   path: "/purchased-rooms",
      //   name: "RoomPurchased",
      //   component: () => import("src/views/admin/rooms/RoomPurchased"),
      //   meta: {
      //     title: i18n.t("page_room_items.page_title"),
      //     middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
      //     permissionCode: { parent: MENU_CODE.ROOMS, code: MENU_CODE.ROOMS },
      //   }
      // },
      //#endregion

      //#region Room Items
      // {
      //   path: "/purchased-objects",
      //   name: "ListObjects",
      //   component: () => import("src/views/admin/room-items/RoomItemPurchased"),
      //   meta: {
      //     title: i18n.t("page_room_items.page_title"),
      //     middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
      //     permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECTS },
      //   }
      // },
      {
        path: "/list-objects",
        name: "ListObjects",
        component: () => import("src/views/admin/room-items/ListObjects"),
        meta: {
          title: i18n.t("page_room_items.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECTS },
        }
      },
      // {
      //   path: "/company-objects",
      //   name: "CompanyObjects",
      //   component: () => import("src/views/admin/room-items/CompanyObjects"),
      //   meta: {
      //     title: i18n.t("page_room_items.page_title"),
      //     middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
      //     permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.COMPANY_ROOM_OBJECTS },
      //   }
      // },
       {
        path: "/register-objects",
        name: "RegisterObjects",
        component: () => import("src/views/admin/room-items/RegisterObject"),
        meta: {
          title: i18n.t("page_room_items.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.COMPANY_ROOM_OBJECTS },
        }
      },
      {
        path: "/edit-objects/:id",
        name: "EditObjects",
        component: () => import("src/views/admin/room-items/EditObject"),
        meta: {
          title: i18n.t("page_room_items.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.COMPANY_ROOM_OBJECTS },
        }
      },
      {
        path: "/room-objects/shopping",
        name: "RoomItemBuy",
        component: () => import("src/views/admin/room-items/RoomItemBuy"),
        meta: {
          title: i18n.t("page_room_items_buy.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECT_SHOP },
        },
      },
      {
        path: "/purchased-objects/detail/:id",
        name: "RoomItemDetailBuy",
        component: () => import("src/views/admin/room-items/RoomItemDetailBuy"),
        meta: {
          title: i18n.t("page_room_items_buy.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECT_SHOP },
        },
      },
      {
        path: "/room-objects/detail/:id",
        name: "RoomItemDetail",
        component: () => import("src/views/admin/room-items/RoomItemDetail"),
        meta: {
          title: i18n.t("page_room_items_detail.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECTS },
        },
      },
      {
        path: "/purchased-objects/history/:id",
        name: "RoomItemHistory",
        component: () => import("src/views/admin/room-items/RoomItemHistory"),
        meta: {
          title: i18n.t("page_room_items_detail.page_title"),
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECTS },
        },
      },
      //#endregion

      //#region Profile
      {
        path: "/profile",
        name: "ProfileDetail",
        component: () => import("src/views/admin/profile/ProfileDetail"),
        meta: {
          title: i18n.t("side_bar.company_profile"),
          // title: i18n.t("side_bar.profile_detail"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PROFILE_DETAIL, code: MENU_CODE.PROFILE_DETAIL },
        },
      },
      {
        path: "/profile/edit",
        name: "ProfileEdit",
        component: () => import("src/views/admin/profile/ProfileEdit"),
        meta: {
          title: i18n.t("side_bar.edit_profile"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PROFILE_DETAIL, code: MENU_CODE.PROFILE_DETAIL },
        },
      },
      {
        path: "/owner/edit",
        name: "OwnerEdit",
        component: () => import("src/views/admin/profile/OwnerEdit"),
        meta: {
          title: i18n.t("owner_edit"),
          middleware: [auth, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.PROFILE_DETAIL, code: MENU_CODE.PROFILE_DETAIL },
        },
      },
      //#endregion

      //#region PR Channel
      {
        path: "/pr-channels",
        name: "PRChannelList",
        component: () => import("src/views/admin/prchannel/PRChannelList"),
        meta: {
          title: i18n.t("pr_channel_list"),
          functions: [PLAN_FUNCTION.PR_CHANNEL],
          middleware: [auth,trialPeriod, checkFunction, checkPermission403],
          permissionCode: { parent: MENU_CODE.PR_CHANNEL, code: MENU_CODE.PR_CHANNEL_LIST },
        },
      },
      {
        path: "/pr-channels/add",
        name: "PRChannelAdd",
        component: () => import("src/views/admin/prchannel/PRChannelAdd"),
        meta: {
          title: i18n.t("create_pr_channel"),
          functions: [PLAN_FUNCTION.PR_CHANNEL],
          middleware: [auth, trialPeriod, checkFunction, checkPermission403],
          permissionCode: { parent: MENU_CODE.PR_CHANNEL, code: MENU_CODE.CREATE_PR_CHANNEL },
        },
      },
      {
        path: "/pr-channels/:id/edit",
        name: "PRChannelEdit",
        component: () => import("src/views/admin/prchannel/PRChannelEdit"),
        meta: {
          title: i18n.t("create_pr_channel"),
          functions: [PLAN_FUNCTION.PR_CHANNEL],
          middleware: [auth,trialPeriod, checkFunction, checkPermission403],
          permissionCode: { parent: MENU_CODE.PR_CHANNEL, code: MENU_CODE.PR_CHANNEL_LIST },
        },
      },
      //#endregion

      //#region Operator management
      {
        path: "/operator-group",
        name: "OperatorGroupList",
        component: () =>
          import("src/views/admin/operator-management/GroupList"),
        meta: {
          functions: ["operation_permissions"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR_GROUP },
        },
      },
      {
        path: "/operator-group/create",
        name: "OperatorGroupAdd",
        component: () => import("src/views/admin/operator-management/GroupAdd"),
        meta: {
          functions: ["operation_permissions"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR_GROUP },
        },
      },
      {
        path: "/operator-group/edit/:id",
        name: "OperatorGroupEdit",
        component: () =>
          import("src/views/admin/operator-management/GroupEdit"),
        meta: {
          functions: ["operation_permissions"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR_GROUP },
        },
      },
      {
        path: "/operator",
        name: "OperatorList",
        component: () =>
          import("src/views/admin/operator-management/OperatorList"),
        meta: {
          functions: ["operation_permissions"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR },
        },
      },
      {
        path: "/invite-operator",
        name: "InviteOperatorList",
        component: () =>
          import("src/views/admin/operator-management/InviteOperatorList"),
        meta: {
          functions: ["operation_members"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR },
        },
      },
      {
        path: "/invite-operator/create/:id",
        name: "InviteOperatorAdd",
        component: () =>
          import("src/views/admin/operator-management/InviteOperatorAdd"),
        meta: {
          functions: ["operation_members"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR },
        },
      },
      {
        path: "/invite-operator/edit/:id",
        name: "InviteOperatorEdit",
        component: () =>
          import("src/views/admin/operator-management/InviteOperatorEdit"),
        meta: {
          functions: ["operation_members"],
          middleware: [auth, trialPeriod, checkOwner, checkFunction, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.OPERATOR_MANAGEMENT, code: MENU_CODE.OPERATOR },
        },
      },
      //#endregion

      {
        path: "/cart",
        name: "CartDetail",
        component: () => import("src/views/admin/shopping-cart/Cart"),
        meta: {
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECT_SHOP },
        },
      },

      {
        path: "/cart/checkout",
        name: "CartCheckout",
        component: () => import("src/views/admin/shopping-cart/CartCheckout"),
        meta: {
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECT_SHOP },
        },
      },

      {
        path: "/purchase-success",
        name: "PurchaseSuccess",
        component: () =>
          import("src/views/admin/shopping-cart/PurchaseSuccess"),
        meta: {
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.ROOM_OBJECTS, code: MENU_CODE.ROOM_OBJECT_SHOP },
        },
      },

      //#region empaty Drive
      {
        path: "/empaty-drive/shared",
        name: "SharedFolder",
        component: () => import("src/views/admin/empaty-drive/SharedFolder"),
        functions: ["empaty_drive_permissions"],
        meta: {
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.EMPATY_DRIVE, code: MENU_CODE.EMPATY_DRIVE },
        },
      },
      {
        path: "/empaty-drive/personal",
        name: "Personal",
        component: () => import("src/views/admin/empaty-drive/Personal"),
        functions: ["empaty_drive_permissions"],
        meta: {
          middleware: [auth, trialPeriod, checkOrgStatus, checkPermission403],
          permissionCode: { parent: MENU_CODE.EMPATY_DRIVE, code: MENU_CODE.EMPATY_DRIVE },
        },
      },
      //#endregion
    ],
  },
  //#endregion
  //#region No require auth
  {
    path: "/",
    component: () => import("src/views/auth/Auth"),
    children: [
      {
        path: "login",
        name: "Login",
        component: () => import("src/views/auth/Login"),
        meta: {
          title: i18n.t("side_bar.login"),
          middleware: [guest],
        },
      },
      {
        path: "login/verify",
        name: "LoginVerify",
        component: () => import("src/views/auth/LoginVerify"),
        props: true,
        meta: {
          title: i18n.t("side_bar.login_verify"),
          middleware: [guest],
        },
      },
      {
        path: "/password/reset",
        name: "PasswordReset",
        component: () => import("src/views/auth/PasswordReset"),
        meta: {
          title: i18n.t("side_bar.password_reset"),
          middleware: [guest],
        },
      },
      {
        path: "/password/complete",
        name: "PasswordComplete",
        component: () => import("src/views/auth/PasswordComplete"),
        props: true,
        meta: {
          title: i18n.t("side_bar.password_complete"),
          middleware: [guest],
        },
      },
      {
        path: "/company/register",
        name: "RegisterCompany",
        component: () => import("src/views/auth/RegisterCompany"),
        meta: {
          title: i18n.t("side_bar.register_company"),
          middleware: [guest],
        },
      },
      {
        path: "/2fa/setup",
        name: "TwoFactorAuthSetup",
        component: () => import("src/views/auth/TwoFactorAuthSetup"),
        props: true,
        meta: {
          title: "Setup Two-Factor Authentication",
        },
      },
    ],
  },
  //#endregion

  //#region Public pages (no auth required)
  {
    path: "/rooms/tour",
    name: "RoomTour",
    component: () => import("src/views/admin/rooms/RoomTour"),
    meta: {
      title: i18n.t("side_bar.room_3d_assets"),
    },
  },
  //#endregion
];

const router = new Router({
  routes,
  base: "/",
  mode: "history",
  linkActiveClass: "active",
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

// router.beforeEach(VueRouteMiddleware());

router.beforeEach(async (to, from, next) => {
  const middlewares = to.meta?.middleware || [];
  if (middlewares.length === 0) {
    return next();
  }

  await runMiddlewares(to, from, next, middlewares);
});

export default router;
